// src/services/comisionistasService.js
// CRUD de comisionistas con doc espejo automático en `proveedores`.
//
// Regla de sincronización:
//   Al crear  → se crea un proveedor con tipo:"comisionista" y referencia cruzada.
//   Al editar → se actualiza el proveedor espejo con nombre/teléfono/email/rfc/activo.
//   El proveedor espejo NO se borra al desactivar — sólo se marca activo:false.

import {
  collection, addDoc, getDoc, getDocs, doc, updateDoc,
  onSnapshot, query, where, serverTimestamp,
} from "firebase/firestore";
import { db } from "../Firebase/firebaseconfig";
import { createProveedor, updateProveedor } from "./proveedoresService";

const COLL = "comisionistas";

// ─── Realtime list ───────────────────────────────────────────────────────────

export function listenComisionistas({ soloActivos = false } = {}, cb) {
  const base = collection(db, COLL);
  const qy = soloActivos ? query(base, where("activo", "==", true)) : base;
  return onSnapshot(qy, (snap) =>
    cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
  );
}

// ─── Read ─────────────────────────────────────────────────────────────────────

export async function getComisionista(id) {
  const snap = await getDoc(doc(db, COLL, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

// ─── Search (cliente-side filter sobre activos) ───────────────────────────────

export async function searchComisionistas(term = "", max = 10) {
  const qy = query(collection(db, COLL), where("activo", "==", true));
  const snap = await getDocs(qy);
  const t = (term || "").trim().toLowerCase();
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((c) => {
      if (!t) return true;
      const texto = `${c.nombre || ""} ${c.apellidos || ""} ${c.email || ""} ${c.telefono || ""}`.toLowerCase();
      return texto.includes(t);
    })
    .slice(0, max);
}

// ─── Create (+ espejo proveedor) ─────────────────────────────────────────────

export async function createComisionista(data) {
  const nombreCompleto = [data.nombre, data.apellidos].filter(Boolean).join(" ").trim();

  // 1. Crear doc espejo en proveedores
  const provRef = await createProveedor({
    nombre: nombreCompleto || "Comisionista",
    telefono: data.telefono || "",
    email: data.email || "",
    rfc: data.rfc || "",
    tipo: "comisionista",
    activo: data.activo ?? true,
    notas: `Comisionista externo — creado automáticamente por el módulo de comisiones.`,
  });

  // 2. Crear doc comisionista
  const payload = buildPayload(data, provRef.id);
  const comRef = await addDoc(collection(db, COLL), payload);

  // 3. Backlink en proveedor
  await updateProveedor(provRef.id, { comisionistaId: comRef.id });

  return comRef;
}

// ─── Update (+ sync espejo) ───────────────────────────────────────────────────

export async function updateComisionista(id, data) {
  const current = await getComisionista(id);
  const patch = {
    ...data,
    // Normalizar email si se está editando
    ...(data.email !== undefined && { email: (data.email || "").trim().toLowerCase() }),
    updatedAt: serverTimestamp(),
  };
  await updateDoc(doc(db, COLL, id), patch);

  // Sincronizar proveedor espejo
  if (current?.proveedorEspejoId) {
    const nombreActualizado = [
      data.nombre ?? current.nombre,
      data.apellidos ?? current.apellidos,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

    await updateProveedor(current.proveedorEspejoId, {
      nombre: nombreActualizado,
      telefono: data.telefono ?? current.telefono ?? "",
      email: data.email ?? current.email ?? "",
      rfc: data.rfc ?? current.rfc ?? "",
      activo: data.activo ?? current.activo ?? true,
    });
  }
}

// ─── Toggle activo ────────────────────────────────────────────────────────────

export async function toggleComisionistaActivo(id, activo) {
  await updateComisionista(id, { activo });
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

function buildPayload(data, proveedorEspejoId) {
  return {
    nombre: (data.nombre || "").trim(),
    apellidos: (data.apellidos || "").trim(),
    telefono: data.telefono || "",
    // Email normalizado a minúsculas para que el portal pueda hacer lookup exacto.
    email: (data.email || "").trim().toLowerCase(),
    rfc: (data.rfc || "").toUpperCase().trim(),
    cuentaBancaria: data.cuentaBancaria || "",
    banco: data.banco || "",
    tipo: data.tipo || "externo",
    uid: data.uid || null,
    proveedorEspejoId: proveedorEspejoId || null,
    tarifaPorDefecto: Number(data.tarifaPorDefecto) || 0.03,
    esquemaPorDefecto: data.esquemaPorDefecto || "enganche-50-mensualidades-50",
    activo: data.activo ?? true,
    creado: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
}
